import { LayoutModeSchema, type LayoutMode, type PeerId } from '@live-stream/shared'
import { readKey, writeKey, type KeyValueStore } from '../storage.js'
import { patch, readonly, writable, type ReadableStore } from '../store.js'
import type { LayoutScene } from './layout.js'

const LAYOUT_KEY = 'ls_layout'

/**
 * What this client alone has decided about its stage.
 *
 * Two things, and neither leaves the browser: the layout mode (Meet treats
 * layout as personal, and so do we; the room's `layoutMode` is the host's
 * default for people who have not chosen) and the local pin. "Pin for me" and
 * "Spotlight for everyone" are two different acts, and the second belongs to
 * the room because the server decides it.
 */

export interface StageState {
  layoutMode: LayoutMode
  localPinnedPeerId: PeerId | null
  /**
   * What the stage is showing — the scene, not the rectangles. The recorder
   * lays the same scene out again for the recording's own frame, so the
   * recording follows the meeting because it IS the meeting's layout, at
   * the recording's size.
   */
  scene: LayoutScene | null
}

export class Stage {
  private readonly store: ReturnType<typeof writable<StageState>>
  readonly state: ReadableStore<StageState>
  /**
   * The live video elements by tile, for the recorder to paint from.
   * Deliberately not state: a map of elements is not something to re-render on.
   */
  private readonly videos = new Map<string, HTMLVideoElement>()

  constructor(private readonly local: KeyValueStore | null) {
    const parsed = LayoutModeSchema.safeParse(readKey(local, LAYOUT_KEY))
    this.store = writable<StageState>({
      layoutMode: parsed.success ? parsed.data : 'auto',
      localPinnedPeerId: null,
      scene: null,
    })
    this.state = readonly(this.store)
  }

  setLayoutMode(mode: LayoutMode): void {
    patch(this.store, { layoutMode: mode })
    writeKey(this.local, LAYOUT_KEY, mode)
  }

  togglePin(peerId: PeerId): void {
    const pinned = this.store.get().localPinnedPeerId
    patch(this.store, { localPinnedPeerId: pinned === peerId ? null : peerId })
  }

  clearPin(): void {
    patch(this.store, { localPinnedPeerId: null })
  }

  setScene(scene: LayoutScene | null): void {
    patch(this.store, { scene })
  }

  /** Tiles register under the peer id; the presentation under `screen:<peerId>`. */
  registerVideo(key: string, el: HTMLVideoElement): void {
    this.videos.set(key, el)
  }

  unregisterVideo(key: string, el?: HTMLVideoElement): void {
    if (!el || this.videos.get(key) === el) this.videos.delete(key)
  }

  videoElement(key: string): HTMLVideoElement | null {
    return this.videos.get(key) ?? null
  }
}
